// `/archie` — the ArchIE headless matter-intake demo (fn-69.3).
//
// The visual payoff of fn-69: ONE deployed `matter-creator` agent driven
// programmatically (the fn-69.2 driver), rendered as a status strip — NOT
// a chat transcript. Upload/pick or paste a document → watch the agent
// work as shimmering status text → handle the restricted-form (yes/no)
// cards → see the created matter.
//
// This is a NEW, self-contained surface. It does NOT thread a new mode
// through the shared `<Chat>`/reducers (fn-69.4 owns chat mode). It is
// styled with the Smokeball design tokens (scoped under `.archie-root`),
// distinct from the operator console's warm-cream chrome.

import './archie-headless.css';

import { useHeadlessRun } from './use-headless-run.js';
import { ActivityLog, IntakeForm, RestrictedFormCard, ResultPanel, ShimmerStatus } from './view.js';

/**
 * The default `matter-creator` agent id. Env-overridable via
 * `VITE_MATTER_CREATOR_AGENT_ID` (per-environment) and falling back to the
 * dev-aus deployed agent (config unchanged — the fn-69 thesis) so the demo
 * works out of the box. Still editable in the intake form. The
 * `import.meta.env` access is narrowed defensively for node/test where it
 * is absent (mirrors `readBffOriginFromEnv`).
 */
function defaultAgentId(): string {
  const fallback = 'b36e920e-4356-45c1-9ff5-02dfa7f76ee2';
  const meta = (import.meta as ImportMeta & { env?: Record<string, unknown> }).env;
  const raw = meta?.VITE_MATTER_CREATOR_AGENT_ID;
  return typeof raw === 'string' && raw.length > 0 ? raw : fallback;
}
const DEFAULT_AGENT_ID = defaultAgentId();

export function ArchieHeadlessPage(): React.ReactElement {
  const run = useHeadlessRun();
  const { state } = run;
  const started = state.phase !== 'idle';
  const active = state.phase === 'starting' || state.phase === 'running';

  return (
    <section className="archie-root">
      <header className="archie-header">
        <div>
          <h1>ArchIE — automated matter intake</h1>
          <p className="archie-subtitle">
            One agent, driven headless. Upload or paste an intake document and watch it assemble a
            Smokeball matter.
          </p>
        </div>
        {started ? (
          <button type="button" className="archie-btn archie-btn-ghost" onClick={run.reset}>
            Start over
          </button>
        ) : null}
      </header>

      {!started ? (
        <IntakeForm
          defaultAgentId={DEFAULT_AGENT_ID}
          uploading={run.uploading}
          {...(run.uploadError !== undefined ? { uploadError: run.uploadError } : {})}
          {...(run.uploadedFile !== undefined ? { uploadedFile: run.uploadedFile } : {})}
          onPickFile={run.uploadFile}
          onStart={run.start}
          disabled={false}
        />
      ) : (
        <div className="archie-run">
          <ShimmerStatus
            status={state.status}
            isFallback={state.statusIsFallback}
            active={active}
          />

          {state.mountedRealPaths.length > 0 ? (
            <div className="archie-mounts" aria-label="Mounted files">
              <span className="archie-mounts-label">Mounted</span>
              {state.mountedRealPaths.map((p) => (
                <code key={p}>{p}</code>
              ))}
            </div>
          ) : null}

          <ActivityLog history={state.history} tools={state.tools} />

          {state.phase === 'awaiting-gate' && state.pendingGate !== undefined ? (
            <RestrictedFormCard
              gate={state.pendingGate}
              busy={run.gateBusy}
              onConfirm={(override) => run.resolveGate('approved', override)}
              onReject={() => run.resolveGate('rejected')}
            />
          ) : null}

          {state.result !== undefined ? <ResultPanel result={state.result} /> : null}
        </div>
      )}
    </section>
  );
}
